import { type FixedDepartureActivityOut, type FixedDepartureDetail } from '@utazzo/shared';
import { UzButton, UzEmptyState, UzInput, UzMoneyInput, UzSelect } from '@utazzo/ui';
import { Ticket, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateFixedDepartureActivity,
  useDeleteFixedDepartureActivity,
  useUpdateFixedDepartureActivity,
} from '../use-fixed-departures';

import { useCurrenciesList } from '@/features/currencies/use-currencies';

const NONE = '__none__';

const paiseToRupees = (paise: string | null | undefined): number =>
  paise === null || paise === undefined ? 0 : Number(paise) / 100;
const rupeesToPaise = (rupees: number | null): string => String(Math.round((rupees ?? 0) * 100));

interface ActivityDraft {
  tourName: string;
  adultPrice: number;
  childPrice: number;
  infantPrice: number;
  currencyId: string;
}

const emptyDraft = (): ActivityDraft => ({
  tourName: '',
  adultPrice: 0,
  childPrice: 0,
  infantPrice: 0,
  currencyId: NONE,
});

const toDraft = (a: FixedDepartureActivityOut): ActivityDraft => ({
  tourName: a.tourName,
  adultPrice: paiseToRupees(a.adultPricePaise),
  childPrice: paiseToRupees(a.childPricePaise),
  infantPrice: paiseToRupees(a.infantPricePaise),
  currencyId: a.currencyId ?? NONE,
});

const toPayload = (d: ActivityDraft) => ({
  tourName: d.tourName.trim(),
  adultPricePaise: rupeesToPaise(d.adultPrice),
  childPricePaise: rupeesToPaise(d.childPrice),
  infantPricePaise: rupeesToPaise(d.infantPrice),
  currencyId: d.currencyId === NONE ? null : d.currencyId,
});

const fmtPaise = (paise: string, code: string | null): string => {
  const amount = Number(paise) / 100;
  return `${code ?? ''} ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

/**
 * Optional-activity OPTION POOL. Entered by hand (no activity-master lookup, to
 * match the legacy screen) and priced per pax type in its own currency.
 */
export const FdActivitiesStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const createActivity = useCreateFixedDepartureActivity(fd.id);
  const updateActivity = useUpdateFixedDepartureActivity(fd.id);
  const deleteActivity = useDeleteFixedDepartureActivity(fd.id);
  const currencies = useCurrenciesList({ page: 1, pageSize: 200, sortDir: 'asc', isActive: true });

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<ActivityDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ActivityDraft>(emptyDraft);

  const currencyOptions = [
    { value: NONE, label: 'Default currency' },
    ...(currencies.data?.data ?? []).map((c) => ({ value: c.id, label: c.code })),
  ];

  const valid = (d: ActivityDraft): boolean => d.tourName.trim().length > 0;

  const submitNew = async (): Promise<void> => {
    if (!valid(draft)) return;
    await createActivity.mutateAsync(toPayload(draft));
    setDraft(emptyDraft());
    setAdding(false);
  };

  const submitEdit = async (): Promise<void> => {
    if (!editingId || !valid(editDraft)) return;
    await updateActivity.mutateAsync({ activityId: editingId, payload: toPayload(editDraft) });
    setEditingId(null);
  };

  const form = (
    d: ActivityDraft,
    set: (patch: Partial<ActivityDraft>) => void,
  ): React.JSX.Element => (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr_120px]">
      <UzInput
        label="Tour name"
        placeholder="e.g. Kathakali cultural show"
        value={d.tourName}
        onChange={(e) => set({ tourName: e.target.value })}
      />
      <UzMoneyInput
        label="Adult"
        value={d.adultPrice}
        onValueChange={(v) => set({ adultPrice: v ?? 0 })}
      />
      <UzMoneyInput
        label="Child"
        value={d.childPrice}
        onValueChange={(v) => set({ childPrice: v ?? 0 })}
      />
      <UzMoneyInput
        label="Infant"
        value={d.infantPrice}
        onValueChange={(v) => set({ infantPrice: v ?? 0 })}
      />
      <UzSelect
        label="Currency"
        value={d.currencyId}
        onValueChange={(v) => set({ currencyId: v })}
        options={currencyOptions}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">Optional activities</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Paid add-ons the traveller can choose. Priced per pax type; the itinerary picks which
            ones apply.
          </p>
        </div>
        {!adding && (
          <UzButton size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add activity
          </UzButton>
        )}
      </div>

      {adding && (
        <section className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4">
          {form(draft, (patch) => setDraft((p) => ({ ...p, ...patch })))}
          <div className="mt-3 flex items-center gap-2">
            <UzButton
              size="sm"
              onClick={submitNew}
              disabled={!valid(draft)}
              loading={createActivity.isPending}
            >
              Add activity
            </UzButton>
            <UzButton
              size="sm"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setDraft(emptyDraft());
              }}
            >
              Cancel
            </UzButton>
          </div>
        </section>
      )}

      {fd.activities.length === 0 && !adding ? (
        <UzEmptyState
          icon={<Ticket className="h-6 w-6" aria-hidden />}
          title="No optional activities yet"
          description="Add the paid extras this fixed departure offers."
        />
      ) : (
        <div className="space-y-2">
          {fd.activities.map((activity) =>
            editingId === activity.id ? (
              <section key={activity.id} className="rounded-lg border border-zinc-300 bg-white p-4">
                {form(editDraft, (patch) => setEditDraft((p) => ({ ...p, ...patch })))}
                <div className="mt-3 flex items-center gap-2">
                  <UzButton
                    size="sm"
                    onClick={submitEdit}
                    disabled={!valid(editDraft)}
                    loading={updateActivity.isPending}
                  >
                    Save
                  </UzButton>
                  <UzButton size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </UzButton>
                </div>
              </section>
            ) : (
              <div
                key={activity.id}
                className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white px-4 py-3"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    setEditingId(activity.id);
                    setEditDraft(toDraft(activity));
                  }}
                >
                  <div className="truncate text-sm font-medium text-zinc-900">
                    {activity.tourName}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
                    <span>Adult {fmtPaise(activity.adultPricePaise, activity.currencyCode)}</span>
                    <span className="text-zinc-300">|</span>
                    <span>Child {fmtPaise(activity.childPricePaise, activity.currencyCode)}</span>
                    <span className="text-zinc-300">|</span>
                    <span>Infant {fmtPaise(activity.infantPricePaise, activity.currencyCode)}</span>
                  </div>
                </button>
                <UzButton
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-zinc-400 hover:text-red-600"
                  onClick={() => deleteActivity.mutate(activity.id)}
                  aria-label={`Remove ${activity.tourName}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </UzButton>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};
